<script setup lang="ts">
// components
import AppDropdown from '~/components/redesign/AppDropdown';
import AppCheckbox from '~/components/redesign/AppCheckbox.vue';

// directives
import { vOnClickOutside } from '@vueuse/components';

// types
import type {
  AppDropdownProps,
  AppDropdownSlots,
} from '~/components/redesign/AppDropdown';

type ModelValue = unknown[];
type CompareFn = (value: unknown) => boolean;
type UpdateFn = (value: unknown, checked: boolean) => void;
type MultiselectDropdownProps = AppDropdownProps & {
  overwriteUpdate?: UpdateFn;
  overwriteCompare?: CompareFn;
};

const props = withDefaults(defineProps<MultiselectDropdownProps>(), {
  options: () => [],
});

defineSlots<Omit<AppDropdownSlots, 'button'>>();

const focused = ref<boolean>(false);
const setFocused = (value: boolean) => {
  focused.value = value;
};

const modelValue = defineModel<ModelValue>({ default: () => [] });

const compare: CompareFn = (value: unknown) => modelValue.value.includes(value);
const update: UpdateFn = (newValue, checked) => {
  let updatedModelValue: ModelValue = [];

  const valueIndex = modelValue.value.findIndex(value => value === newValue);

  if (checked) {
    updatedModelValue = [...modelValue.value, newValue];
  } else {
    updatedModelValue = modelValue.value.filter(
      (_value, index) => valueIndex !== index,
    );
  }

  modelValue.value = updatedModelValue;
};

const checkboxState = computed(() => {
  if (modelValue.value.length === props.options.length) return true;
  else if (!modelValue.value.length) return false;
  else return null;
});

const toggleAll = (value: boolean) => {
  modelValue.value = value ? props.options : [];
};
</script>

<template>
  <div
    v-on-click-outside="() => setFocused(false)"
    data-component-name="MultiselectDropdown"
    class="base"
  >
    <div class="wrapper">
      <AppDropdown.Button
        v-model:focused="focused"
        :has-value="Boolean(modelValue.length)"
        :label
        :size
        :fill
        :required
        :disabled
        :error
      >
        <slot :value="modelValue" name="value">
          {{ modelValue.join(', ') }}
        </slot>
      </AppDropdown.Button>

      <transition name="fade">
        <AppDropdown.List v-if="focused" :options>
          <template #prepend>
            <AppDropdown.ListItem tag="label">
              <AppCheckbox
                :model-value="checkboxState"
                @click.prevent
                @update:model-value="toggleAll"
              />

              <span>{{ $t('All') }}</span>
            </AppDropdown.ListItem>
          </template>

          <template #item="{ value }">
            <AppDropdown.ListItem tag="label">
              <AppCheckbox
                :model-value="(overwriteCompare || compare)(value)"
                @update:model-value="(overwriteUpdate || update)(value, $event)"
                @click.prevent
              />

              <slot :value name="item">
                {{ value }}
              </slot>
            </AppDropdown.ListItem>
          </template>
        </AppDropdown.List>
      </transition>
    </div>
  </div>
</template>

<style
  src="~/components/redesign/AppDropdown/AppDropdown.scss"
  lang="scss"
  scoped
/>

<style scoped lang="scss">
@use '_/mixins/flex';
@use '_/mixins/size';

@mixin applyToBase {
  [data-component-name='AppDropdown.ListItem'] {
    [data-component-name='Buttons.Base'] {
      @content;
    }
  }
}

[data-component-name='MultiselectDropdown'] {
  &:deep([data-component-name='AppDropdown.List']) {
    @include applyToBase {
      @include flex.center-start;
    }

    ul {
      li {
        @include applyToBase {
          padding-left: 2rem;
        }
      }
    }
  }
}
</style>
